import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alertApi, type AlertItem } from '@/lib/api';
import { AlertTriangle, CheckCircle, X, Circle } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { PageGuide } from '@/components/PageGuide';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';

export const Route = createFileRoute('/_auth/alerts')({ component: AlertsPage });

type AlertSeverity = AlertItem['severity'];
type AlertStatus   = AlertItem['status'];

const SEV_STYLE: Record<AlertSeverity, { bg: string; text: string; border: string }> = {
  INFO:     { bg: '#06b6d420', text: '#22d3ee', border: '#06b6d440' },
  WARNING:  { bg: '#f59e0b20', text: '#fbbf24', border: '#f59e0b40' },
  ERROR:    { bg: '#ef444420', text: '#f87171', border: '#ef444440' },
  CRITICAL: { bg: '#ef444430', text: '#f87171', border: '#ef444460' },
};

const STATUS_FILTERS: Array<{ value: AlertStatus | ''; label: string }> = [
  { value: 'OPEN',         label: 'Open' },
  { value: '',             label: 'All' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'RESOLVED',    label: 'Resolved' },
];

export default function AlertsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<AlertStatus | ''>('OPEN');

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['alerts', statusFilter],
    queryFn: () => alertApi.list(statusFilter || undefined),
    // no polling — socket invalidates
  });

  // Real-time invalidation via portal socket
  usePortalSocketEvent('alert:new', () => qc.invalidateQueries({ queryKey: ['alerts'] }));
  usePortalSocketEvent('counts:update', () => qc.invalidateQueries({ queryKey: ['alerts'] }));

  const ackMutation = useMutation({
    mutationFn: (id: string) => alertApi.acknowledge(id),
    onSuccess: () => { toast.success('Alert acknowledged'); qc.invalidateQueries({ queryKey: ['alerts'] }); },
  });
  const resolveMutation = useMutation({
    mutationFn: (id: string) => alertApi.resolve(id),
    onSuccess: () => { toast.success('Alert resolved'); qc.invalidateQueries({ queryKey: ['alerts'] }); },
  });

  const openCount = alerts.filter((a) => a.status === 'OPEN').length;

  return (
    <div className="page">
      <PageGuide pageKey="alerts" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Alerts</h1>
            {openCount > 0 && (
              <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#ef444420', color: '#f87171', border: '1px solid #ef444440' }}>
                {openCount} open
              </span>
            )}
          </div>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>LLM failures, integration errors, and configuration issues</p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map((f) => (
          <button key={f.value || 'ALL'} onClick={() => setStatusFilter(f.value)} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: statusFilter === f.value ? 'var(--color-primary)' : '#1e2130',
            background: statusFilter === f.value ? 'var(--color-primary-dim)' : 'transparent',
            color: statusFilter === f.value ? 'var(--color-primary)' : '#64748b',
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {/* ── Empty ── */}
      {!isLoading && alerts.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <CheckCircle size={32} style={{ opacity: 0.5, marginBottom: 12, color: '#34d399' }} />
          <p style={{ margin: 0, fontSize: 14 }}>
            {statusFilter === 'OPEN' ? 'All systems operating normally.' : 'No alerts match this filter.'}
          </p>
        </div>
      )}

      {/* ── Alert cards ── */}
      {!isLoading && alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {alerts.map((alert) => {
            const sev = SEV_STYLE[alert.severity] ?? SEV_STYLE.INFO;
            const isResolved = alert.status === 'RESOLVED';
            return (
              <div
                key={alert.id}
                id={`alert-${alert.id}`}
                style={{
                  borderRadius: 12,
                  border: `1px solid ${sev.border}`,
                  borderLeft: `4px solid ${sev.text}`,
                  background: isResolved ? '#0a0e1c' : sev.bg,
                  padding: '16px 20px',
                  opacity: isResolved ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {/* top row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sev.bg, color: sev.text, border: `1px solid ${sev.border}` }}>
                    {alert.severity === 'CRITICAL' && <AlertTriangle size={11} />}
                    {alert.severity === 'ERROR'    && <X size={11} />}
                    {alert.severity === 'WARNING'  && <AlertTriangle size={11} />}
                    {alert.severity === 'INFO'     && <Circle size={9} fill="currentColor" />}
                    {alert.severity}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', background: '#1e2130', padding: '2px 8px', borderRadius: 8 }}>{alert.source}</span>
                  <span style={{ fontSize: 11, color: '#475569', marginLeft: 'auto' }}>{new Date(alert.createdAt).toLocaleString('en-GB')}</span>
                  {/* actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    {alert.status === 'OPEN' && (
                      <button
                        id={`ack-${alert.id}`}
                        onClick={() => ackMutation.mutate(alert.id)}
                        style={{ padding: '4px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid #f59e0b40', background: '#f59e0b10', color: '#fbbf24' }}
                      >
                        Acknowledge
                      </button>
                    )}
                    {alert.status !== 'RESOLVED' && (
                      <button
                        id={`resolve-${alert.id}`}
                        onClick={() => resolveMutation.mutate(alert.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid #10b98140', background: '#10b98110', color: '#34d399' }}
                      >
                        <CheckCircle size={12} /> Resolve
                      </button>
                    )}
                  </div>
                </div>

                <h3 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>{alert.title}</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{alert.message}</p>

                {alert.resolutionHint && (
                  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: '#0f1221', border: '1px solid #1e2130', fontSize: 12, color: '#64748b' }}>
                    💡 <strong style={{ color: '#94a3b8' }}>Hint:</strong> {alert.resolutionHint}
                  </div>
                )}

                {Object.keys(alert.context).length > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ fontSize: 12, color: '#475569', cursor: 'pointer' }}>Context</summary>
                    <pre style={{ margin: '8px 0 0', padding: '10px 12px', background: '#0a0e1c', borderRadius: 8, fontSize: 11, color: '#64748b', overflow: 'auto', border: '1px solid #1e2130' }}>
                      {JSON.stringify(alert.context, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
